import { type CloudflareAccessClaims, validateCloudflareAccessToken } from "@repo/cloudflare-auth";
import { getCfAccessConfig, getCfAccessUrls, validateCfAccessConfig, getCsrfTokenFromCookie, getCsrfConfig, getRefreshUrl, validateCookieAuth } from "../../lib/cf-access-config";
import React, {
	createContext,
	type ReactNode,
	useContext,
	useEffect,
	useState,
} from "react";

// Authentication context
interface AuthContextType {
	user: CloudflareAccessClaims | null;
	isAuthenticated: boolean;
	isLoading: boolean;
	error: string | null;
	login: () => void;
	logout: () => void;
}

export const AuthContext = createContext<AuthContextType | undefined>(
	undefined,
);

export function useAuth() {
	const context = useContext(AuthContext);
	if (context === undefined) {
		throw new Error("useAuth must be used within an AuthProvider");
	}
	return context;
}

// Cloudflare Access configuration
const CF_ACCESS_CONFIG = getCfAccessConfig();

// Cloudflare Access login URL
const getLoginUrl = () => {
  const { login } = getCfAccessUrls();
  return login(window.location.href);
};

interface RequireAuthProps {
	children: ReactNode;
}

export function RequireAuth({ children }: RequireAuthProps) {
	const [user, setUser] = useState<CloudflareAccessClaims | null>(null);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	// Skip authentication in development mode
	const isDevelopment = import.meta.env.DEV;

	const login = () => { window.location.href = getLoginUrl(); };

	const logout = () => {
		// Clear any stored tokens
		localStorage.removeItem("cf-access-token");
		sessionStorage.removeItem("cf-access-token");

		// Redirect to Cloudflare Access logout
		const { logout } = getCfAccessUrls();
		window.location.href = logout;
	};

	const validateToken = async (_token: string) => {
		try {
			// Cookie-based auth: verify session with a lightweight authenticated call
			const errs = validateCfAccessConfig();
			if (errs.length > 0) { setError(`CF Access config error: ${errs.join(", ")}`); return false; }
			const res = await fetch("/health", { credentials: 'include' });
			if (res.ok) {
				setError(null);
				// Best-effort: decode cookie token client-side for user info if available
				try {
					const cookieName = (import.meta.env.VITE_CF_JWT_COOKIE_NAME as string | undefined) || 'CF_Authorization';
					const cookie = document.cookie
						.split(';')
						.find((c) => c.trim().startsWith(`${cookieName}=`));
					if (cookie) {
						const jwt = decodeURIComponent(cookie.split('=')[1] || '');
						const [, payload] = jwt.split('.') as [string,string,string];
						const json = atob(payload.replace(/-/g,'+').replace(/_/g,'/'));
						setUser(JSON.parse(json));
					}
				} catch {}
				return true;
			}
			setError("Unauthorized");
			return false;
		} catch (err) {
			setError(err instanceof Error ? err.message : "Token validation failed");
			return false;
		}
	};

    // Cookie-first; no local/session storage

	// biome-ignore lint/correctness/useExhaustiveDependencies: intentional minimal deps for auth bootstrap
	useEffect(() => {
		const authenticate = async () => {
			setIsLoading(true);
			setError(null);

			// Skip authentication in development mode
			if (isDevelopment) {
				setUser({ email: "dev@example.com", name: "Development User" } as CloudflareAccessClaims);
				setIsLoading(false);
				return;
			}

			try {
				if (!validateCookieAuth()) {
					setError("No authentication token found");
					setIsLoading(false);
					return;
				}

				const isValid = await validateToken("");
				if (!isValid) {
					setUser(null);
				}
			} catch (err) {
				setError(err instanceof Error ? err.message : "Authentication failed");
				setUser(null);
			} finally {
				setIsLoading(false);
			}
		};

		authenticate();

		// Set up periodic token refresh check (every 5 minutes)
		const interval = setInterval(
			async () => {
				try {
					await fetch(getRefreshUrl(), { method: 'POST', credentials: 'include' });
				} catch {}
			},
			5 * 60 * 1000,
		);

		return () => clearInterval(interval);
	}, []);

	const authContextValue: AuthContextType = {
		user,
		isAuthenticated: !!user,
		isLoading,
		error,
		login,
		logout,
	};

	// Show loading spinner while validating
	if (isLoading) {
		return (
			<div className="min-h-screen flex items-center justify-center bg-gray-50">
				<div className="text-center">
					<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4" />
					<p className="text-gray-600">Validating authentication...</p>
				</div>
			</div>
		);
	}

	// Show error state with login option
	if (error && !user) {
		return (
			<div className="min-h-screen flex items-center justify-center bg-gray-50">
				<div className="max-w-md w-full bg-white shadow-lg rounded-lg p-6">
					<div className="text-center">
						<div className="text-red-500 mb-4">
							<svg
								className="mx-auto h-12 w-12"
								fill="none"
								viewBox="0 0 24 24"
								stroke="currentColor"
								role="img"
								aria-label="Error icon"
							>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth={2}
									d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 19.5c-.77.833.192 2.5 1.732 2.5z"
								/>
							</svg>
						</div>
						<h2 className="text-xl font-semibold text-gray-900 mb-2">
							Authentication Required
						</h2>
						<p className="text-gray-600 mb-6">{error}</p>
						<button
							type="button"
							onClick={login}
							className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
						>
							Sign In with Cloudflare Access
						</button>
					</div>
				</div>
			</div>
		);
	}

	// Render children with auth context
	return (
		<AuthContext.Provider value={authContextValue}>
			{children}
		</AuthContext.Provider>
	);
}
